/**
 * Tide Run's way in: what the mode is, the player's records, and a choice of
 * where to start - the first level of any world they have reached, so a run
 * is 10-30 levels rather than always a grind up from level 1 (docs/TIDE_RUN.md).
 */
import Phaser from 'phaser';
import { drawVerticalGradient } from '../art/backdrop';
import { THEME_BANDS, WORLD_NAMES } from '../data/themes';
import { setDevState } from '../systems/devQuery';
import {
  isTideUnlocked,
  startTideRun,
  tideStartLevels,
  TIDE_UNLOCK_LEVEL,
} from '../systems/TideRun';
import type { SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { IconButton } from './ui/IconButton';
import { pressButton, styleButton } from './ui/menuButton';

const TITLE_Y = IS_PORTRAIT ? 120 : 64;
const BLURB_Y = IS_PORTRAIT ? 200 : 122;
const RECORDS_Y = IS_PORTRAIT ? 300 : 186;
const PROMPT_Y = IS_PORTRAIT ? 380 : 246;
const LIST_TOP = IS_PORTRAIT ? 460 : 312;
const ROW_GAP = IS_PORTRAIT ? 84 : 74;
/** Wide stage: two columns of five worlds. */
const COLUMN_GAP = 540;

export class TideStart extends Phaser.Scene {
  private save!: SaveManager;
  private buttons: Phaser.GameObjects.Text[] = [];
  private actions: Array<() => void> = [];
  private focused = 0;

  constructor() {
    super('TideStart');
  }

  create(): void {
    setDevState({ scene: 'TideStart' });
    this.save = this.registry.get('save') as SaveManager;
    // Grant any Tide Run reward already earned but not yet given.
    this.save.refreshTideRewards();
    this.buttons = [];
    this.actions = [];

    drawVerticalGradient(this, [
      [0x0b1a33, 0],
      [0x12395e, 0.55],
      [0x1f78c8, 1],
    ]);
    new IconButton(this, 56, 56, 'back', () => this.scene.start('LevelMap'), 52);

    this.add
      .text(GAME_WIDTH / 2, TITLE_Y, 'Tide Run', {
        fontFamily: FONT_FAMILY,
        fontSize: IS_PORTRAIT ? '64px' : '56px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 6,
      })
      .setOrigin(0.5);
    this.add
      .text(
        GAME_WIDTH / 2,
        BLURB_Y,
        'The sea is rising! Type to climb away from it.\nClear a level and the next one starts straight away.',
        {
          fontFamily: FONT_FAMILY,
          fontSize: IS_PORTRAIT ? '26px' : '24px',
          color: PALETTE.ice,
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 80 },
          lineSpacing: 6,
        },
      )
      .setOrigin(0.5);

    if (!isTideUnlocked((level) => this.save.isLevelComplete(level))) {
      this.drawLocked();
      this.installInput();
      return;
    }

    this.drawRecords();
    this.add
      .text(GAME_WIDTH / 2, PROMPT_Y, 'Where do you want to start?', {
        fontFamily: FONT_FAMILY,
        fontSize: '30px',
        color: PALETTE.gold,
      })
      .setOrigin(0.5);
    this.drawWorlds();
    this.installInput();
  }

  private drawLocked(): void {
    this.add
      .text(GAME_WIDTH / 2, PROMPT_Y, `Finish level ${TIDE_UNLOCK_LEVEL} to open Tide Run.`, {
        fontFamily: FONT_FAMILY,
        fontSize: '30px',
        color: PALETTE.gold,
      })
      .setOrigin(0.5);
    this.addButton('Back to the map', GAME_WIDTH / 2, LIST_TOP, () => this.scene.start('LevelMap'));
    this.focus(0);
  }

  private drawRecords(): void {
    const records = this.save.tideRecords();
    const text =
      records.runs === 0
        ? 'Your first run! The water matches your own typing speed.'
        : `Furthest: level ${records.furthestLevel}     Most in one run: ${records.mostLevelsInRun}     Tide stars: ${this.save.tideStars()}`;
    this.add
      .text(GAME_WIDTH / 2, RECORDS_Y, text, {
        fontFamily: FONT_FAMILY,
        fontSize: '22px',
        color: PALETTE.snow,
        align: 'center',
        backgroundColor: '#0b1220',
        padding: { x: 18, y: 10 },
        wordWrap: { width: GAME_WIDTH - 60 },
      })
      .setOrigin(0.5);
  }

  private drawWorlds(): void {
    const starts = tideStartLevels((level) => this.save.isLevelUnlocked(level));
    starts.forEach((level, index) => {
      const band = THEME_BANDS.find((row) => row.fromLevel === level);
      if (band === undefined) return;
      const column = IS_PORTRAIT ? 0 : Math.floor(index / 5);
      const row = IS_PORTRAIT ? index : index % 5;
      const twoColumns = !IS_PORTRAIT && starts.length > 5;
      const x = twoColumns ? GAME_WIDTH / 2 + (column - 0.5) * COLUMN_GAP : GAME_WIDTH / 2;
      const label = `${WORLD_NAMES[band.background]}  ·  level ${level}`;
      this.addButton(label, x, LIST_TOP + row * ROW_GAP, () => this.start(level));
    });
    // The newest world reached is the likeliest choice.
    this.focus(Math.max(0, this.buttons.length - 1));
  }

  private addButton(label: string, x: number, y: number, action: () => void): void {
    const index = this.buttons.length;
    const button = this.add
      .text(x, y, label, {
        fontFamily: FONT_FAMILY,
        fontSize: '28px',
        color: PALETTE.snow,
        backgroundColor: '#1b2a41',
        padding: { x: 22, y: 12 },
      })
      .setOrigin(0.5)
      .setFixedSize(IS_PORTRAIT ? GAME_WIDTH - 100 : 480, 0)
      .setAlign('center')
      .setInteractive({ useHandCursor: true });
    button.on('pointerover', () => this.focus(index));
    button.on('pointerup', () => pressButton(button, action));
    this.buttons.push(button);
    this.actions.push(action);
  }

  private start(level: number): void {
    const history = this.save.activeProfile()?.history ?? [];
    this.scene.start('Game', { level, tide: startTideRun(level, history) });
  }

  private focus(index: number): void {
    this.focused = index;
    this.buttons.forEach((button, i) => styleButton(button, i === index));
  }

  private installInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const count = this.buttons.length;
      if (event.key === 'Escape') {
        this.scene.start('LevelMap');
      } else if (count === 0) {
        return;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        this.focus((this.focused + count - 1) % count);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        this.focus((this.focused + 1) % count);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const button = this.buttons[this.focused];
        const action = this.actions[this.focused];
        if (button !== undefined && action !== undefined) pressButton(button, action);
      }
    });
  }
}

/** For screens that offer the way in: true when the player may start a run. */
export function tideOpenFor(save: SaveManager | undefined): boolean {
  if (save === undefined || save.activeProfile() === null) return false;
  return isTideUnlocked((level) => save.isLevelComplete(level));
}

/** Kept beside the scene so the HUD and map agree on the wording. */
export const TIDE_LOCKED_HINT = `Finish level ${TIDE_UNLOCK_LEVEL} to open Tide Run`;
